import axios from "axios";
import type {
  Transport,
  TransportCreate,
  TransportUpdate,
  Auth,
  AuthCreate,
  AuthUpdate,
  Aor,
  AorCreate,
  AorUpdate,
  Endpoint,
  EndpointCreate,
  EndpointUpdate,
  Identify,
  IdentifyCreate,
  IdentifyUpdate,
  DialplanContext,
  DialplanContextCreate,
  DialplanContextUpdate,
  DialplanExtension,
  DialplanExtensionCreate,
  DialplanExtensionUpdate,
  AriApp,
  AriAppCreate,
  AriAppUpdate,
  WebsocketEndpoint,
  WebsocketEndpointCreate,
  WebsocketEndpointUpdate,
  GenerateResult,
  ReloadResult,
  ActiveCall,
  CallLog,
  OriginateCallRequest,
  AuditLog,
  ServerStatus,
  ConfigViewResponse,
  AmiCommandResponse,
  ApiKey,
  ApiKeyCreate,
  ApiKeyCreated,
} from "@/types";

const api = axios.create({ baseURL: "/api" });

function crud<T, C, U>(prefix: string) {
  return {
    list: (skip = 0, limit = 100) =>
      api.get<T[]>(prefix, { params: { skip, limit } }).then((r) => r.data),
    get: (id: string) => api.get<T>(`${prefix}/${id}`).then((r) => r.data),
    create: (data: C) => api.post<T>(prefix, data).then((r) => r.data),
    update: (id: string, data: U) =>
      api.put<T>(`${prefix}/${id}`, data).then((r) => r.data),
    delete: (id: string) => api.delete(`${prefix}/${id}`),
  };
}

export const transportsApi = crud<Transport, TransportCreate, TransportUpdate>(
  "/transports"
);
export const authsApi = crud<Auth, AuthCreate, AuthUpdate>("/auths");
export const aorsApi = crud<Aor, AorCreate, AorUpdate>("/aors");
export const endpointsApi = crud<Endpoint, EndpointCreate, EndpointUpdate>(
  "/endpoints"
);
export const identifiesApi = crud<Identify, IdentifyCreate, IdentifyUpdate>(
  "/identifies"
);
export const contextsApi = crud<
  DialplanContext,
  DialplanContextCreate,
  DialplanContextUpdate
>("/contexts");
export const ariAppsApi = crud<AriApp, AriAppCreate, AriAppUpdate>("/ari-apps");
export const wsEndpointsApi = crud<
  WebsocketEndpoint,
  WebsocketEndpointCreate,
  WebsocketEndpointUpdate
>("/websocket-endpoints");

export const extensionsApi = {
  list: (params?: { context_id?: string; skip?: number; limit?: number }) =>
    api
      .get<DialplanExtension[]>("/extensions", { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<DialplanExtension>(`/extensions/${id}`).then((r) => r.data),
  create: (data: DialplanExtensionCreate) =>
    api.post<DialplanExtension>("/extensions", data).then((r) => r.data),
  update: (id: string, data: DialplanExtensionUpdate) =>
    api
      .put<DialplanExtension>(`/extensions/${id}`, data)
      .then((r) => r.data),
  delete: (id: string) => api.delete(`/extensions/${id}`),
};

export const configApi = {
  generate: (module = "all") =>
    api
      .post<GenerateResult>("/config/generate", null, { params: { module } })
      .then((r) => r.data),
  reload: (module = "all") =>
    api
      .post<ReloadResult>("/config/reload", null, { params: { module } })
      .then((r) => r.data),
};

export interface CallHistoryParams {
  skip?: number;
  limit?: number;
  direction?: string;
  date_from?: string;
  date_to?: string;
}

export const callsApi = {
  active: () => api.get<ActiveCall[]>("/calls/active").then((r) => r.data),
  history: (params: CallHistoryParams = {}) =>
    api.get<CallLog[]>("/calls", { params }).then((r) => r.data),
  historyCount: (params: { direction?: string; date_from?: string; date_to?: string } = {}) =>
    api.get<{ count: number }>("/calls/count", { params }).then((r) => r.data),
  get: (id: string) => api.get<CallLog>(`/calls/${id}`).then((r) => r.data),
  hangup: (channel: string) =>
    api.post(`/calls/${encodeURIComponent(channel)}/hangup`).then((r) => r.data),
  transfer: (channel: string, destination: string) =>
    api
      .post(`/calls/${encodeURIComponent(channel)}/transfer`, { destination })
      .then((r) => r.data),
  originate: (data: OriginateCallRequest) =>
    api.post("/calls/originate", data).then((r) => r.data),
  extensions: () =>
    api.get<string[]>("/calls/extensions").then((r) => r.data),
};

export const auditLogsApi = {
  list: (params?: { skip?: number; limit?: number; table_name?: string; operation?: string }) =>
    api
      .get<AuditLog[]>("/audit-logs", { params })
      .then((r) => r.data),
  count: (params?: { table_name?: string; operation?: string }) =>
    api
      .get<{ count: number }>("/audit-logs/count", { params })
      .then((r) => r.data),
  tables: () => api.get<string[]>("/audit-logs/tables").then((r) => r.data),
};

export const statusApi = {
  get: () => api.get<ServerStatus>("/status").then((r) => r.data),
};

export const configViewApi = {
  get: (module: string) =>
    api.get<ConfigViewResponse>("/config/view", { params: { module } }).then((r) => r.data),
};

export const amiCommandApi = {
  run: (command: string) =>
    api.post<AmiCommandResponse>("/ami/command", { command }).then((r) => r.data),
};

export const logSettingsApi = {
  get: () =>
    api.get<{ debug_level: number; verbose_level: number; pjsip_logger_enabled: boolean }>("/logs/settings").then((r) => r.data),
};

export const apiKeysApi = {
  list: () => api.get<ApiKey[]>("/api-keys/").then((r) => r.data),
  create: (data: ApiKeyCreate) =>
    api.post<ApiKeyCreated>("/api-keys/", data).then((r) => r.data),
  revoke: (id: string) => api.delete(`/api-keys/${id}`),
};
